import { memo, useLayoutEffect } from 'react';
import { useAppDispatch } from '../../hooks';
import { useHandleRootnavComponentDimensionUpdate } from '../../NavLayoutDimensions/hooks/useHandleRootnavComponentDimensionUpdate';
import { useCrmDnDInteraction } from '../../hooks/useCrmDnDInteraction';
import useRemoveNavPlaceholder from '../../hooks/useRemoveNavPlaceholder';
import { updatePageWidthForVerticalNav } from '../../components/VerticalNav/utils/updatePageWidthForVerticalNav';
import { toolbarAndNavDisplayConfig } from '../../overlayDisplayConfig/actions';
import { useHandleNavInappCommMessage } from '../../overlayDisplayConfig/hooks/useHandleNavInappCommMessage';
import { useObserveOverlayChanges } from '../../overlayDisplayConfig/hooks/useObserveOverlayChanges';
import { useUpdateAppOverlayConfigState } from '../../overlayDisplayConfig/hooks/useSendAppOverlayConfigMessage';
import { useUpdateToolbarAndNavDisplayStyles } from '../../overlayDisplayConfig/hooks/useUpdateToolbarAndNavDisplayStyles';
import { OverlayType } from '../../overlayDisplayConfig/interface';
import { addFeedbackModalRoot, addVerticalNavContainer, getCallingContainer, getOrAddGlobalToolbarContainer } from '../../utils/globalToolbarUtils';
import { isFullscreenEditorLayout } from '../../utils/isFullscreenEditor';
import { isGlobalNavAcademyMode } from '../../utils/navEnviro';
import { AlertList } from '../../components/AlertsList';
import { Capabilities } from '../../components/Capabilities';
import FeedbackModal from '../../components/FeedbackModal';
import { GlobalStyles } from '../../components/GlobalStyles';
import GlobalToolbar from '../../components/GlobalToolbar/GlobalToolbar';
import { NavigationComponentsProvider } from '../../components/NavigationComponentsProvider';
import { Portal } from '../../components/Portal';
import { VerticalNav } from '../../components/VerticalNav';
import ErrorBoundary from '../../components/ErrorBoundary';
import { MaybeLoadGrowthPromptsNavigationComponent } from '../../external-contrib/growth-prompts/components';
import { MaybeLoadAcademyNavigationComponents } from '../../external-contrib/academy/components/AcademyNavigationComponents';
import { SetupNotifications } from '../../external-contrib/notifications/components/SetupNotifications';
import CallingRemote from '../../external-contrib/calling-remote/components/CallingRemote';
import { useLoadHelpCta } from '../../external-contrib/in-app-help/hooks/useLoadHelpCta';
import { TrialBannerMaybe } from '../../external-contrib/growth-trials/components/TrialBannerMaybe';
import { useVisibilityPublicApi } from '../../features/public-api/hooks/useVisibilityPublicApi';
import { MaybeGlobalHomeOnboardingTour } from '../../features/global-home-onboarding-tour';
import { NavOnboardingTourSupport } from '../../features/nav-onboarding-tours/components/NavOnboardingTourSupport';

// Name is added at the bottom of the file
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const RootNav = /*#__PURE__*/memo(
// eslint-disable-next-line hubspot-dev/no-unnamed-components
() => {
  const dispatch = useAppDispatch();
  useUpdateAppOverlayConfigState();
  // Injected code to observe for overlays and update the display config state in Redux
  useObserveOverlayChanges();
  // Code that responds to redux display config state changes by adding CSS classes for
  // toolbar, nav & overlay styles related to positioning and stacking
  useUpdateToolbarAndNavDisplayStyles();

  // Full screen editor apps show the editor modal by default. So, they need to access the modal display
  // configuration during the application init. So we update the nav display config here without waiting for
  // a modal to be opened
  if (isFullscreenEditorLayout()) {
    dispatch(toolbarAndNavDisplayConfig({
      isOverlayOpen: true,
      overlayType: OverlayType.MODAL
    }));
  }
  useVisibilityPublicApi();
  useHandleNavInappCommMessage();
  useHandleRootnavComponentDimensionUpdate();
  useCrmDnDInteraction();
  useRemoveNavPlaceholder({
    placeholderType: 'vertical-nav',
    shouldRemovePlaceholder: isGlobalNavAcademyMode()
  });
  useLayoutEffect(() => {
    if (isGlobalNavAcademyMode()) {
      updatePageWidthForVerticalNav({
        isVerticalNavEnabled: false
      });
    }
  }, []);
  useLoadHelpCta(false);
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    boundaryName: "App Navigation Root",
    children: /*#__PURE__*/_jsxs(NavigationComponentsProvider, {
      children: [/*#__PURE__*/_jsx(ErrorBoundary, {
        boundaryName: "Global Styles",
        children: /*#__PURE__*/_jsx(GlobalStyles, {})
      }), !isGlobalNavAcademyMode() && /*#__PURE__*/_jsx(Portal, {
        container: getOrAddGlobalToolbarContainer,
        children: /*#__PURE__*/_jsx(ErrorBoundary, {
          boundaryName: "Global Toolbar",
          children: /*#__PURE__*/_jsx(GlobalToolbar, {})
        })
      }, "global-toolbar"), !isGlobalNavAcademyMode() && /*#__PURE__*/_jsx(Portal, {
        container: addFeedbackModalRoot,
        children: /*#__PURE__*/_jsx(ErrorBoundary, {
          boundaryName: "Feedback Modal",
          children: /*#__PURE__*/_jsx(FeedbackModal, {})
        })
      }, "feedback-modal"), /*#__PURE__*/_jsx(Portal, {
        container: addVerticalNavContainer,
        children: /*#__PURE__*/_jsx(ErrorBoundary, {
          boundaryName: "Vertical Nav",
          children: /*#__PURE__*/_jsx(VerticalNav, {})
        })
      }, "vertical-nav"), isGlobalNavAcademyMode() && /*#__PURE__*/_jsx(Portal, {
        container: getCallingContainer,
        children: /*#__PURE__*/_jsx(ErrorBoundary, {
          boundaryName: "Calling Remote @RootNav",
          children: /*#__PURE__*/_jsx(CallingRemote, {})
        })
      }, "calling-remote"), /*#__PURE__*/_jsx("div", {
        id: "nav-object-create-root"
      }), /*#__PURE__*/_jsx(ErrorBoundary, {
        boundaryName: "Alert List",
        children: /*#__PURE__*/_jsx(AlertList, {})
      }), /*#__PURE__*/_jsx(ErrorBoundary, {
        boundaryName: "Setup Notifications",
        children: /*#__PURE__*/_jsx(SetupNotifications, {})
      }), /*#__PURE__*/_jsx(ErrorBoundary, {
        boundaryName: "growth-prompts-loader",
        children: /*#__PURE__*/_jsx(MaybeLoadGrowthPromptsNavigationComponent, {})
      }), /*#__PURE__*/_jsx(ErrorBoundary, {
        boundaryName: "Academy Loader",
        children: /*#__PURE__*/_jsx(MaybeLoadAcademyNavigationComponents, {})
      }), /*#__PURE__*/_jsx(ErrorBoundary, {
        boundaryName: "Trial Banner Loader",
        children: /*#__PURE__*/_jsx(TrialBannerMaybe, {})
      }), /*#__PURE__*/_jsx(ErrorBoundary, {
        boundaryName: "Global Home Onboarding Tour",
        children: /*#__PURE__*/_jsx(MaybeGlobalHomeOnboardingTour, {})
      }), /*#__PURE__*/_jsx(ErrorBoundary, {
        boundaryName: "Nav Onboarding Tours",
        children: /*#__PURE__*/_jsx(NavOnboardingTourSupport, {})
      }), /*#__PURE__*/_jsx(ErrorBoundary, {
        boundaryName: "Capabilities Handlers",
        children: /*#__PURE__*/_jsx(Capabilities, {})
      })]
    })
  });
});
RootNav.displayName = 'RootNav';